/**
 * PortalInductionsList — "Invites" page
 * Unified list of ALL invites (Employee / Contractor / Visitor) and Projects.
 * Each invite has its own unique date range and digital badge.
 */

import React, { useEffect, useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { PageHeader, SectionCard, StatusPill, EmptyState } from '@/components/portal/PortalUI';
import {
  ClipboardList, CheckCircle2, ListChecks,
  Clock, BookOpen, AlertCircle, Calendar, MapPin,
  FolderKanban, Users, Send, HardHat, UserCheck, Eye, TimerOff,
} from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { supabase } from '@shared/supabaseClient';

/** Map person_type to banner config */
const PERSON_TYPE_CONFIG = {
  employee:   { label: 'Employee',   icon: UserCheck, bar: 'bg-blue-600',   text: 'text-white' },
  contractor: { label: 'Contractor', icon: HardHat,   bar: 'bg-amber-500',  text: 'text-white' },
  visitor:    { label: 'Visitor',    icon: Eye,        bar: 'bg-purple-600', text: 'text-white' },
};

const PROJECT_TYPE_CONFIG = {
  lead:   { label: 'Project',      icon: FolderKanban, bar: 'bg-teal-600',  text: 'text-white' },
  member: { label: 'Project — Team Member', icon: Users, bar: 'bg-teal-600', text: 'text-white' },
};

/** Coloured banner strip rendered at the very top of a card */
const TypeBanner = ({ label, icon: Icon, bar, text }) => (
  <div className={`-mx-5 -mt-5 mb-4 flex items-center gap-2 rounded-t-xl px-4 py-2 ${bar}`}>
    <Icon className={`h-3.5 w-3.5 shrink-0 ${text}`} />
    <span className={`text-xs font-bold uppercase tracking-widest ${text}`}>{label}</span>
  </div>
);

/** Format a date string or Date to a readable label */
const fmtDate = (d) => {
  if (!d) return null;
  try {
    return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return null; }
};

const fmtDateRange = (start, end) => {
  const s = fmtDate(start);
  const e = fmtDate(end);
  if (s && e) return `${s} – ${e}`;
  return s || e || null;
};

const getInviteStatusInfo = (s) => {
  if (s === 'approved') return { label: 'Approved', tone: 'success', icon: CheckCircle2, bg: 'bg-green-50 border-green-300', textColor: 'text-green-800', desc: 'Induction approved — your pass is active.' };
  if (s === 'submitted') return { label: 'Submitted', tone: 'warning', icon: Clock, bg: 'bg-amber-50 border-amber-300', textColor: 'text-amber-800', desc: 'Submitted — awaiting team leader review.' };
  if (s === 'pending_acknowledgement') return { label: 'Re-confirm Required', tone: 'info', icon: BookOpen, bg: 'bg-blue-50 border-blue-200', textColor: 'text-blue-800', desc: 'Please review and re-confirm this induction.' };
  if (s === 'in_progress') return { label: 'In Progress', tone: 'info', icon: ListChecks, bg: 'bg-sky-50 border-sky-200', textColor: 'text-sky-800', desc: 'Induction started — continue where you left off.' };
  return { label: 'Not Started', tone: 'neutral', icon: ClipboardList, bg: 'bg-gray-50 border-gray-200', textColor: 'text-gray-800', desc: 'Complete this induction to activate your digital badge for this visit.' };
};

const getProjectStatusInfo = (s) => {
  if (s === 'approved' || s === 'active')   return { label: 'Approved', tone: 'success' };
  if (s === 'submitted_awaiting_approval')   return { label: 'Pending Approval', tone: 'warning' };
  if (s === 'draft_in_progress')             return { label: 'In Progress', tone: 'info' };
  if (s === 'draft_pending_supplier')        return { label: 'Awaiting Setup', tone: 'info' };
  if (s === 'completed' || s === 'archived') return { label: 'Completed', tone: 'success' };
  if (s === 'rejected')                      return { label: 'Rejected', tone: 'danger' };
  return { label: s, tone: 'neutral' };
};

const isInviteDone = (s) => s === 'approved' || s === 'submitted';
const isProjectDone = (s) => s === 'approved' || s === 'active' || s === 'completed' || s === 'archived';

/** An invite is expired if:
 *  - status is explicitly 'expired' or 'cancelled', OR
 *  - the visit date (valid_from) has passed and the induction was never completed
 */
const isInviteExpired = (invite) => {
  if (invite.status === 'expired' || invite.status === 'cancelled') return true;
  const notDone = !isInviteDone(invite.status);
  const visitDate = invite.valid_from || invite.created_at;
  if (notDone && visitDate) {
    const d = new Date(visitDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d < today;
  }
  return false;
};

const PortalInductionsList = () => {
  const { session } = useOutletContext();
  const navigate = useNavigate();

  const [invites, setInvites]   = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const loadAll = async () => {
    const email = session?.person?.email || session?.email;
    if (!email) { setLoading(false); return; }
    setLoading(true);
    setError(null);

    try {
      await Promise.all([loadInvites(email), loadProjects(email)]);
    } finally {
      setLoading(false);
    }
  };

  const loadInvites = async (email) => {
    try {
      await supabase.rpc('portal_ensure_team_member_inductions', { p_email: email });

      const { data: allData, error: allErr } = await supabase.rpc('portal_get_all_invites_by_email', { p_email: email });

      let data = allData;
      if (allErr || !allData) {
        const { data: fallback, error: fbErr } = await supabase.rpc('portal_get_induction_summary_by_email', { p_email: email });
        if (fbErr) throw fbErr;
        data = fallback;
      }

      setInvites(
        (data || [])
          .filter(row => !row.public_reference?.startsWith('TM-'))
          .map(row => ({
            submission_id:    row.submission_id,
            title:            row.brief_title || row.title,
            status:           row.status,
            depot_name:       row.depot_name,
            public_reference: row.public_reference,
            brief_version:    row.brief_version || null,
            valid_from:       row.valid_from || null,
            created_at:       row.created_at || null,
            person_type:      row.person_type || null,
          }))
      );
    } catch (err) {
      console.error('Error loading invites:', err);
      setError('Failed to load invites.');
    }
  };

  const loadProjects = async (email) => {
    try {
      const { data: leadProjects } = await supabase
        .from('projects')
        .select('id, project_name, status, estimated_start_date, estimated_end_date, project_lead_email, depots:site_id(name)')
        .eq('project_lead_email', email)
        .order('created_at', { ascending: false });

      const { data: memberships } = await supabase
        .from('project_team_members')
        .select('project_id')
        .eq('person_email', email);

      let teamProjects = [];
      if (memberships?.length) {
        const leadIds = new Set((leadProjects || []).map(p => p.id));
        const teamOnlyIds = memberships.map(m => m.project_id).filter(id => !leadIds.has(id));
        if (teamOnlyIds.length) {
          const { data: tp } = await supabase
            .from('projects')
            .select('id, project_name, status, estimated_start_date, estimated_end_date, project_lead_email, depots:site_id(name)')
            .in('id', teamOnlyIds)
            .order('created_at', { ascending: false });
          teamProjects = (tp || []).map(p => ({ ...p, _isTeamMember: true }));
        }
      }

      setProjects([...(leadProjects || []), ...teamProjects]);
    } catch (err) {
      console.error('Error loading projects:', err);
    }
  };

  // Refresh invites on status change events from detail pages
  useEffect(() => {
    const email = session?.person?.email || session?.email;
    const handler = () => { if (email) loadInvites(email); };
    window.addEventListener('induction-status-changed', handler);
    return () => window.removeEventListener('induction-status-changed', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  // ── Derived counts ──────────────────────────────────────────────────────────
  const expiredInvites     = invites.filter(i => isInviteExpired(i));
  const expiredInviteIds   = new Set(expiredInvites.map(i => i.submission_id));
  const outstandingInvites = invites.filter(i => !isInviteDone(i.status) && !expiredInviteIds.has(i.submission_id));
  const completedInvites   = invites.filter(i => isInviteDone(i.status));
  const outstandingProjects = projects.filter(p => !isProjectDone(p.status));
  const completedProjects   = projects.filter(p => isProjectDone(p.status));
  const totalOutstanding = outstandingInvites.length + outstandingProjects.length;
  const totalCompleted   = completedInvites.length   + completedProjects.length;
  const totalAll         = invites.length + projects.length;

  // ── Card components ─────────────────────────────────────────────────────────
  const InviteCard = ({ invite, dimmed = false }) => {
    const info      = getInviteStatusInfo(invite.status);
    const visitDate = fmtDate(invite.valid_from) || fmtDate(invite.created_at);
    const Icon      = info.icon;
    const typeCfg = PERSON_TYPE_CONFIG[invite.person_type];
    return (
      <SectionCard className={dimmed ? 'opacity-60' : ''}>
        {typeCfg && <TypeBanner label={typeCfg.label} icon={typeCfg.icon} bar={dimmed ? 'bg-gray-400' : typeCfg.bar} text={typeCfg.text} />}

        <div className="mb-4">
          <h3 className="text-base font-bold text-foreground">{invite.title || 'Site Visit Invite'}</h3>
          {invite.depot_name && <p className="text-sm font-medium text-muted-foreground mt-1">{invite.depot_name}</p>}
        </div>

        {/* Status banner */}
        <div className={`mb-3 flex items-start gap-2 rounded-lg ${info.bg} border px-3 py-2`}>
          <Icon className={`h-4 w-4 ${info.textColor} flex-shrink-0 mt-0.5`} />
          <p className={`text-xs font-bold ${info.textColor}`}>{info.desc}</p>
        </div>

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-3">
          <StatusPill tone={info.tone} label={info.label} />
          {visitDate && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Calendar className="h-3 w-3" />{visitDate}
            </span>
          )}
          {invite.depot_name && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <MapPin className="h-3 w-3" />{invite.depot_name}
            </span>
          )}
          {invite.brief_version && (
            <span className="text-xs font-mono bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">v{invite.brief_version}</span>
          )}
          {invite.public_reference && (
            <span className="text-xs text-muted-foreground">Ref: {invite.public_reference}</span>
          )}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button size="sm" onClick={() => {
            const done = invite.status === 'approved' || invite.status === 'submitted';
            navigate(done
              ? `/portal/inductions/${invite.submission_id}`
              : `/portal/inductions/${invite.submission_id}/setup`,
            );
          }}>
            <ClipboardList className="h-3.5 w-3.5 mr-1.5" />
            Open Invite
          </Button>
        </div>
      </SectionCard>
    );
  };

  const ProjectCard = ({ project }) => {
    const info      = getProjectStatusInfo(project.status);
    const dateRange = fmtDateRange(project.estimated_start_date, project.estimated_end_date);
    const pTypeCfg = project._isTeamMember ? PROJECT_TYPE_CONFIG.member : PROJECT_TYPE_CONFIG.lead;
    return (
      <SectionCard>
        <TypeBanner label={pTypeCfg.label} icon={pTypeCfg.icon} bar={pTypeCfg.bar} text={pTypeCfg.text} />

        <div className="mb-4">
          <h3 className="text-base font-bold text-foreground">{project.project_name}</h3>
          {project.depots?.name && <p className="text-sm font-medium text-muted-foreground mt-1">{project.depots.name}</p>}
        </div>

        {/* Status banner */}
        {project.status === 'submitted_awaiting_approval' ? (
          <div className="mb-3 flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-300 px-3 py-2">
            <Send className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs font-bold text-blue-800">Submitted — awaiting admin review.</p>
          </div>
        ) : (project.status === 'approved' || project.status === 'active') ? (
          <div className="mb-3 flex items-start gap-2 rounded-lg bg-green-50 border border-green-300 px-3 py-2">
            <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs font-bold text-green-800">Project approved and active.</p>
          </div>
        ) : (
          <div className="mb-3 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
            <Clock className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs font-bold text-amber-800">Open the setup guide to complete your project submission.</p>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <StatusPill tone={info.tone} label={info.label} />
          {dateRange && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Calendar className="h-3 w-3" />{dateRange}
            </span>
          )}
          {project.depots?.name && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <MapPin className="h-3 w-3" />{project.depots.name}
            </span>
          )}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button size="sm" onClick={() => navigate(`/portal/projects/${project.id}`)}>
            <FolderKanban className="h-3.5 w-3.5 mr-1.5" />
            Open Project
          </Button>
        </div>
      </SectionCard>
    );
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Invites" subtitle="Your site visit invitations and projects." />
        <div className="text-center py-12 text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Invites" subtitle="Your site visit invitations and projects — employee, contractor, visitor and project types." />

      {totalAll > 0 && (
        <div className="rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 flex items-center gap-3">
          <ClipboardList className="h-4 w-4 text-primary flex-shrink-0" />
          <p className="text-sm text-foreground">
            <span className="font-semibold">Select an invite or project</span> to view the induction brief, complete any required actions, and access your digital badge.
          </p>
        </div>
      )}

      {/* Summary bar */}
      {totalAll > 0 && (
        <SectionCard
          title="Summary"
          subtitle={`${totalAll} total — ${totalCompleted} completed, ${totalOutstanding} outstanding`}
        >
          <div className="flex items-center gap-4">
            <div className="flex-1 bg-gray-200 rounded-full h-3">
              <div
                className="bg-green-500 h-3 rounded-full transition-all duration-500"
                style={{ width: `${totalAll > 0 ? (totalCompleted / totalAll) * 100 : 0}%` }}
              />
            </div>
            <span className="text-sm font-medium text-foreground">{totalCompleted}/{totalAll}</span>
          </div>

          {/* Type breakdown */}
          <div className="mt-3 flex flex-wrap gap-2">
            {invites.filter(i => i.person_type === 'employee').length > 0 && (
              <span className="text-xs bg-blue-100 text-blue-800 border border-blue-300 rounded-full px-2 py-0.5 font-medium">
                {invites.filter(i => i.person_type === 'employee').length} Employee
              </span>
            )}
            {invites.filter(i => i.person_type === 'contractor').length > 0 && (
              <span className="text-xs bg-amber-100 text-amber-800 border border-amber-300 rounded-full px-2 py-0.5 font-medium">
                {invites.filter(i => i.person_type === 'contractor').length} Contractor
              </span>
            )}
            {invites.filter(i => i.person_type === 'visitor').length > 0 && (
              <span className="text-xs bg-purple-100 text-purple-800 border border-purple-300 rounded-full px-2 py-0.5 font-medium">
                {invites.filter(i => i.person_type === 'visitor').length} Visitor
              </span>
            )}
            {projects.length > 0 && (
              <span className="text-xs bg-teal-100 text-teal-800 border border-teal-300 rounded-full px-2 py-0.5 font-medium">
                {projects.length} Project
              </span>
            )}
          </div>
        </SectionCard>
      )}

      {/* Outstanding */}
      {(outstandingInvites.length > 0 || outstandingProjects.length > 0) && (
        <>
          <h2 className="text-lg font-semibold text-foreground">Outstanding</h2>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {outstandingInvites.map(invite => <InviteCard key={invite.submission_id} invite={invite} />)}
            {outstandingProjects.map(project => <ProjectCard key={project.id} project={project} />)}
          </div>
        </>
      )}

      {/* Completed */}
      {(completedInvites.length > 0 || completedProjects.length > 0) && (
        <>
          <h2 className="text-lg font-semibold text-foreground">Completed</h2>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {completedInvites.map(invite => <InviteCard key={invite.submission_id} invite={invite} />)}
            {completedProjects.map(project => <ProjectCard key={project.id} project={project} />)}
          </div>
        </>
      )}

      {/* Expired */}
      {expiredInvites.length > 0 && (
        <>
          <h2 className="text-lg font-semibold text-muted-foreground flex items-center gap-2">
            <TimerOff className="h-5 w-5" />
            Expired
          </h2>
          <p className="-mt-4 text-sm text-muted-foreground">These invites were not completed before the visit date. You can still open them if needed.</p>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {expiredInvites.map(invite => <InviteCard key={invite.submission_id} invite={invite} dimmed />)}
          </div>
        </>
      )}

      {/* Empty state */}
      {totalAll === 0 && !error && (
        <EmptyState
          icon={ClipboardList}
          title="No invites yet"
          description="When the site team sends you an invite — as an employee, contractor, visitor or project lead — it will appear here."
        />
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
    </div>
  );
};

export default PortalInductionsList;
